/**
 * @fileoverview Cascade Functions - カスケード管理関数群
 */

import { drainPostSettle } from './activation-queue.js';
import { collectAllMatches } from './board-evaluation.js';
// Boardインスタンスはパラメータで渡される
import { removePieces, applyGravityAndFill } from './pieces.js';
import { applySpecialPieceRules } from './specials/manager.js';

// モジュールレベル変数でチェーン管理

let currentChain = 1;
const MAX_CYCLES = 50;

/**
 * チェーンカウントをリセット
 */
export function resetChain() {
  currentChain = 1;
}

/**
 * チェーンカウント増加
 * @returns {number} 新しいチェーンカウント
 */
export function incrementChain() {
  return ++currentChain;
}

/**
 * 現在のチェーンカウント取得
 * @returns {number} チェーンカウント
 */
export function getCurrentChain() {
  return currentChain;
}

// カスケード状態管理

/**
 * 連鎖サイクルの共通処理コア
 * @param {Object} board - Board instance
 * @returns {boolean} 連鎖継続の可否（true: 継続, false: 終了）
 */
async function cascadeCycleCore(board) {
  // カスケードステップ開始

  // 1. 盤面から全マッチを探索（共通ヘルパー使用）
  const allMatches = collectAllMatches(board);

  // 2. 削除対象の決定（マッチ優先ルール）
  let piecesToRemove = [];

  if (allMatches.length > 0) {
    // 通常マッチがあれば優先処理
    // pending特殊ピースは次サイクルに持ち越し（キューはそのまま）
    piecesToRemove = await applySpecialPieceRules(board, allMatches);
  } else {
    // 通常マッチがない場合のみ、Post-Settle発動待ち特殊ピースをチェック
    const queuedIds = drainPostSettle(); // ← マッチがない場合のみ消費
    const pendingSpecialPieces = [];

    for (const pieceId of queuedIds) {
      const result = board.getById(pieceId);
      if (result && result.piece?.specialType) {
        // ID基準で再バインド（落下で座標が変わっても安全）
        pendingSpecialPieces.push({
          ...result.piece,
          position: [result.row, result.col],
        });
      }
      // result が null = ピースが既に消滅している → スキップ（安全弁）
    }

    if (pendingSpecialPieces.length > 0) {
      piecesToRemove = pendingSpecialPieces;
    }
  }

  // 3. 終了判定
  if (!piecesToRemove || piecesToRemove.length === 0) {
    // カスケードステップ終了
    return false; // 連鎖終了
  }

  // 4. 削除処理
  const chainForThisCycle = getCurrentChain();
  await removePieces(board, piecesToRemove, chainForThisCycle);

  // 5. 重力と補充（settled）
  await applyGravityAndFill(board);

  // 6. サイクル完了後にチェインを増加
  incrementChain();

  // 重力アニメーション後にカスケードステップ終了

  return true; // 連鎖継続
}

/**
 * 明示的な連鎖処理ループ
 * 無限ループ防止のため maxCycles を超えたら強制終了
 */
async function processCascadeLoop(board, maxCyclesOverride = null) {
  // 状態は呼び出し元でCascadingに設定済み
  const maxCycles =
    Number.isInteger(maxCyclesOverride) && maxCyclesOverride > 0 ? maxCyclesOverride : MAX_CYCLES;

  // 共通化されたサイクルコアを使用したループ
  for (let cycle = 0; cycle < maxCycles; cycle++) {
    const shouldContinue = await cascadeCycleCore(board);
    if (!shouldContinue) {
      break;
    }
  }
}

/**
 * 初期削除セットからカスケードを実行するユーティリティ
 * @param {Object} board - Board instance
 * @param {Array<Object>} initialPieces
 * @param {number|null} maxCyclesOverride
 */
export async function runCascadeFromRemovals(board, initialPieces, maxCyclesOverride = null) {
  const pieces = Array.isArray(initialPieces) ? initialPieces : [];

  // 処理状態管理は呼び出し元が担当

  // カスケードロジック開始
  resetChain();

  if (pieces.length > 0) {
    // 初期削除にも現在のチェインを適用
    const chainForInitial = getCurrentChain();
    await removePieces(board, pieces, chainForInitial);
    await applyGravityAndFill(board);
    // 初期サイクル完了としてチェインを増加
    incrementChain();
  }

  await processCascadeLoop(board, maxCyclesOverride);

  // 状態リセットは呼び出し元が担当
}
